const SHOPIFY_DOMAIN_SUFFIX = ".myshopify.com";
const BIGCOMMERCE_DOMAIN_SUFFIX = ".mybigcommerce.com";
const BIGCOMMERCE_STORE_PREFIX = "store-";
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const SHOPIFY_STORE_SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

interface ShopifyConnectionMappingLike {
  id?: number | string | null;
  connection_id?: string | null;
  nango_connection_id?: string | null;
  account_id?: string | null;
  account_name?: string | null;
  store_name?: string | null;
  shop_name?: string | null;
  shop_domain?: string | null;
  shopDomain?: string | null;
  store_url?: string | null;
  storeUrl?: string | null;
  external_account_id?: string | null;
  externalAccountId?: string | null;
}

interface ShopifyConnectionIntegrationLike {
  id?: number | string | null;
  mappings?: readonly ShopifyConnectionMappingLike[] | null;
  profile_data?: Record<string, unknown> | null;
  external_account_id?: string | null;
  externalAccountId?: string | null;
  external_account_name?: string | null;
  externalAccountName?: string | null;
}

interface ShopifyDisconnectMatch {
  connectionId?: string;
  workspaceIntegrationId?: string;
  shopDomain?: string;
}

const normalizeShopifyHost = (rawValue: unknown) => {
  if (typeof rawValue !== "string" && typeof rawValue !== "number") {
    return "";
  }

  return (
    String(rawValue)
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, "")
      .replace(/^\/+/, "")
      .split(/[/?#]/)[0]
      ?.replace(/\.+$/, "") || ""
  );
};

/**
 * Normalizes a Shopify domain returned by an integration API.
 *
 * Unlike normalizeShopifyStoreDomainInput, this deliberately rejects display
 * names, UUIDs, and arbitrary connection ids. Those values must never be
 * turned into a synthetic `<value>.myshopify.com` disconnect target.
 */
export const normalizeShopifyConnectionDomainCandidate = (
  rawValue: unknown,
  options: { allowBareStoreSlug?: boolean } = {},
): string | undefined => {
  const host = normalizeShopifyHost(rawValue);
  if (!host) return undefined;

  if (host.endsWith(SHOPIFY_DOMAIN_SUFFIX)) {
    const storeSlug = host.slice(0, -SHOPIFY_DOMAIN_SUFFIX.length);
    return SHOPIFY_STORE_SLUG_PATTERN.test(storeSlug) &&
      !UUID_PATTERN.test(storeSlug)
      ? `${storeSlug}${SHOPIFY_DOMAIN_SUFFIX}`
      : undefined;
  }

  if (
    options.allowBareStoreSlug &&
    !host.includes(".") &&
    !UUID_PATTERN.test(host) &&
    SHOPIFY_STORE_SLUG_PATTERN.test(host)
  ) {
    return `${host}${SHOPIFY_DOMAIN_SUFFIX}`;
  }

  return undefined;
};

const firstShopifyDomain = (
  candidates: Array<{ value: unknown; allowBareStoreSlug?: boolean }>,
) => {
  for (const candidate of candidates) {
    const domain = normalizeShopifyConnectionDomainCandidate(candidate.value, {
      allowBareStoreSlug: candidate.allowBareStoreSlug,
    });
    if (domain) return domain;
  }

  return undefined;
};

export const resolveShopifyConnectionDomain = ({
  integration,
  mapping,
}: {
  integration?: ShopifyConnectionIntegrationLike | null;
  mapping?: ShopifyConnectionMappingLike | null;
}): string | undefined => {
  const profile = integration?.profile_data || {};

  return firstShopifyDomain([
    { value: mapping?.shop_domain, allowBareStoreSlug: true },
    { value: mapping?.shopDomain, allowBareStoreSlug: true },
    { value: mapping?.store_url, allowBareStoreSlug: true },
    { value: mapping?.storeUrl, allowBareStoreSlug: true },
    { value: mapping?.connection_id },
    { value: mapping?.nango_connection_id },
    { value: mapping?.account_id },
    { value: mapping?.external_account_id },
    { value: mapping?.externalAccountId },
    // Display-oriented fields are accepted only when they already contain a
    // complete Shopify domain. Bare labels such as "Main" are never promoted
    // into a disconnect target.
    { value: mapping?.store_name },
    { value: mapping?.shop_name },
    { value: mapping?.account_name },
    { value: profile.shop_domain, allowBareStoreSlug: true },
    { value: profile.shopDomain, allowBareStoreSlug: true },
    { value: profile.store_url, allowBareStoreSlug: true },
    { value: profile.storeUrl, allowBareStoreSlug: true },
    { value: profile.account_name },
    { value: profile.accountName },
    { value: integration?.external_account_id },
    { value: integration?.externalAccountId },
    { value: integration?.external_account_name },
    { value: integration?.externalAccountName },
  ]);
};

/** Resolves one exact Shopify connection; never falls back to another mapping. */
export const resolveShopifyDisconnectDomain = (
  integration?: ShopifyConnectionIntegrationLike | null,
  match: ShopifyDisconnectMatch = {},
): string | undefined => {
  const explicitDomain = normalizeShopifyConnectionDomainCandidate(
    match.shopDomain,
    { allowBareStoreSlug: true },
  );
  if (explicitDomain) return explicitDomain;

  const mappings = integration?.mappings || [];
  const normalizedWorkspaceIntegrationId = String(
    match.workspaceIntegrationId || "",
  );
  const normalizedConnectionId = String(match.connectionId || "");
  const selectedMapping = mappings.find((mapping) => {
    if (
      normalizedWorkspaceIntegrationId &&
      String(mapping.id || "") === normalizedWorkspaceIntegrationId
    ) {
      return true;
    }

    return Boolean(
      normalizedConnectionId &&
      [mapping.connection_id, mapping.nango_connection_id].some(
        (value) => String(value || "") === normalizedConnectionId,
      ),
    );
  });

  if (selectedMapping) {
    return resolveShopifyConnectionDomain({
      integration,
      mapping: selectedMapping,
    });
  }

  const domainFromConnectionId = normalizeShopifyConnectionDomainCandidate(
    match.connectionId,
  );
  if (domainFromConnectionId) return domainFromConnectionId;

  const hasInstanceSelector = Boolean(
    match.workspaceIntegrationId || match.connectionId,
  );
  if (hasInstanceSelector || mappings.length > 1) return undefined;

  return resolveShopifyConnectionDomain({
    integration,
    mapping: mappings[0],
  });
};

export const normalizeShopifyStoreDomainInput = (rawStoreUrl: string) => {
  const normalizedInput = rawStoreUrl
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/^\/+/, "");

  const host = normalizedInput.split(/[/?#]/)[0]?.replace(/\.+$/, "") || "";

  if (!host) {
    return "";
  }

  if (host.endsWith(SHOPIFY_DOMAIN_SUFFIX)) {
    return host;
  }

  return `${host}${SHOPIFY_DOMAIN_SUFFIX}`;
};

export const normalizeStoreUrlTextInput = (
  rawStoreUrl: string,
  platform?: string | null,
) => {
  const normalizedInput = rawStoreUrl
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/^\/+/, "");

  let host = normalizedInput.split(/[/?#]/)[0]?.replace(/\.+$/, "") || "";

  if (!host) {
    return "";
  }

  if (platform === "Shopify") {
    return host.endsWith(SHOPIFY_DOMAIN_SUFFIX)
      ? host.slice(0, -SHOPIFY_DOMAIN_SUFFIX.length)
      : host;
  }

  if (platform === "BigCommerce") {
    if (host.endsWith(BIGCOMMERCE_DOMAIN_SUFFIX)) {
      host = host.slice(0, -BIGCOMMERCE_DOMAIN_SUFFIX.length);
    }

    return host.startsWith(BIGCOMMERCE_STORE_PREFIX)
      ? host.slice(BIGCOMMERCE_STORE_PREFIX.length)
      : host;
  }

  return host;
};
